import React from 'react';

import { DocumentNode } from '@apollo/client';

import { callbackHooks } from '@nxte-nl/callback-hooks';

import {
  MostChosenGiftcardProductsPresentational,
  NUMBER_TO_SHOW_TOP_BLOCKS_INITIALLY_ON_DESKTOP,
  NUMBER_TO_SHOW_TOP_BLOCKS_INITIALLY_ON_LAPTOP,
  NUMBER_TO_SHOW_TOP_BLOCKS_INITIALLY_ON_MOBILE,
} from '../../components/MostChosenProducts/MostChosenProducts';
import { STATIC_REDEEM_TOKEN } from '../../constants/redeem';
import {
  CheckoutMessages,
  GlobalMessages,
  ProductsListMessages,
} from '../../types/Messages';
import { LayoutExtension } from '../../types/ResponsiveState';
import {
  ProductsChunk,
  ProductsResponse,
  TopBlock,
  TopProductsResponse,
} from '../../types/apiCompoundProduct';
import {
  BlankGiftCardPackProduct,
  DigitalWrapper,
  DonationProduct,
  GiftCardProductsPageFilterInput,
  GiftPackingProduct,
  GiftcardProduct,
  Paging,
  PagingInput,
  PersonalMessageProduct,
  ProductType,
  TaxonomyFilterInput,
  VocabularyID,
  WrapperProduct,
} from '../../types/apiTypes';
import { ProductListItemPresentational, ProductListItemType } from '../../types/common';
import { filterHooks } from '../filterHooks/filterHooks';
import { layoutHooks } from '../layoutHooks/layoutHooks';
import { messageHooks } from '../messageHooks/messageHooks';
import { getBlocksQueueHooks } from '../queryHooks/getBlocksQueueHooks/getBlocksQueueHooks';
import { getProductsHooks } from '../queryHooks/getProductsHooks/getProductsHooks';
import { queryStringHooks } from '../queryStringHooks/queryStringHooks';

export const NUMBER_TO_SHOW_TOP_PRODUCTS_ON_DESKTOP = 4;
export const NUMBER_TO_SHOW_TOP_PRODUCTS_ON_DESKTOP_SMALL = 2;
export const NUMBER_TO_SHOW_TOP_PRODUCTS_ON_MOBILE = 2;
export const NUMBER_TO_SHOW_PRODUCTS_ON_DESKTOP = 16;
export const NUMBER_TO_SHOW_PRODUCTS_ON_DESKTOP_SMALL = 17;
export const NUMBER_TO_SHOW_PRODUCTS_ON_MOBILE = 16;

export const NumberToShowTopProducts = {
  [LayoutExtension.DesktopLarge]: NUMBER_TO_SHOW_TOP_PRODUCTS_ON_DESKTOP,
  [LayoutExtension.DesktopSmall]: NUMBER_TO_SHOW_TOP_PRODUCTS_ON_DESKTOP_SMALL,
  [LayoutExtension.Tablet]: NUMBER_TO_SHOW_TOP_PRODUCTS_ON_MOBILE,
  [LayoutExtension.Mobile]: NUMBER_TO_SHOW_TOP_PRODUCTS_ON_MOBILE,
};
export const NumberToShowProducts = {
  [LayoutExtension.DesktopLarge]: NUMBER_TO_SHOW_PRODUCTS_ON_DESKTOP,
  [LayoutExtension.DesktopSmall]: NUMBER_TO_SHOW_PRODUCTS_ON_DESKTOP_SMALL,
  [LayoutExtension.Tablet]: NUMBER_TO_SHOW_PRODUCTS_ON_MOBILE,
  [LayoutExtension.Mobile]: NUMBER_TO_SHOW_PRODUCTS_ON_MOBILE,
};

export interface TypeNameEntity {
  __typename?: string;
}

export type UseProductsParams = {
  filters: TaxonomyFilterInput[] | null;
  isQuery_v1?: boolean;
  page: number;
  passEncodedRedeemToken?: boolean;
  query: DocumentNode;
  toLoadProducts: boolean;
};

export type UseGetProductsVariablesParams = {
  filterByPage: GiftCardProductsPageFilterInput | null;
  filters: TaxonomyFilterInput[] | null;
  isRedeemPage: boolean;
  paging: PagingInput;
};

export const productsHooks = {
  useBlocksQueueHandleResponse<T extends TopBlock,>(
    setTopBlocks: React.Dispatch<React.SetStateAction<T[]>>,
  ) {
    const setBlocksQueueMessage = messageHooks.useNext(ProductsListMessages.BlocksQueue);
    return React.useCallback((response: TopProductsResponse<T> | null) => {
      if (!response) {
        return;
      }
      const { data } = response;
      /**
       * Theoretically content manager can add any type of product in admin panel
       * To avoid potential render's bugs, all items should be filtered
       * The blockType is only one property that exists in every implemented interface of product
       */
      const filteredTopBlocks = data.filter(({ blockType }) => !!blockType);

      setTopBlocks(filteredTopBlocks);
      setBlocksQueueMessage(filteredTopBlocks);
    }, [setBlocksQueueMessage, setTopBlocks]);
  },

  useBlocksQueue<T extends TopBlock,>(
    filters: TaxonomyFilterInput[] | null,
    queueID: string | null = null,
    toLoadProducts?: boolean,
  ) {
    const [topBlocks, setTopBlocks] = React.useState<T[]>([]);

    const filterEmpty = filterHooks.useFilterEmptyFilters();
    const params = React.useMemo(() => {
      if (!queueID || !filters) {
        return;
      }

      return {
        queueID,
        filters: {
          filterByTaxonomy: filterEmpty(filters),
        },
      };
    }, [filterEmpty, filters, queueID]);

    const handleResponse = productsHooks.useBlocksQueueHandleResponse(setTopBlocks);
    const { executeRequest, loading } = getBlocksQueueHooks
      .useGetBlocksQueueQuery( handleResponse );
    const setBlocksQueueLoading = messageHooks
      .useNextWithValue(ProductsListMessages.BlocksQueueLoading, loading);
    callbackHooks.useEffectCallback(setBlocksQueueLoading);

    React.useEffect(() => {
      if (!toLoadProducts || !params) {
        return;
      }
      executeRequest({ variables: params });
    }, [executeRequest, toLoadProducts, params]);

    return { loading, topBlocks, params, handleResponse };
  },

  useProductsPerPage() {
    const layoutExtension = layoutHooks.useLayoutExtension();

    const topProductsPerPage = React.useMemo(() => {
      return NumberToShowTopProducts[layoutExtension || LayoutExtension.DesktopLarge];
    }, [layoutExtension]);

    const productsPerPage = React.useMemo(() => {
      return NumberToShowProducts[layoutExtension || LayoutExtension.DesktopLarge];
    }, [layoutExtension]);

    return { topProductsPerPage, productsPerPage };
  },

  useGiftcardProducts(
    products?: TypeNameEntity[],
  ): GiftcardProduct[] | DonationProduct[] | undefined {
    return React.useMemo(() => {
      return products?.filter(
        i => i?.__typename === 'GiftcardProduct' || i?.__typename === 'DonationProduct',
      ) as GiftcardProduct[] | DonationProduct[];
    }, [products]);
  },

  useMostChosenGiftcardProducts(mostChosen: MostChosenGiftcardProductsPresentational[] | null) {
    const layoutExtension = layoutHooks.useLayoutExtension();
    const products = productsHooks.useGiftcardProducts(mostChosen ?? undefined);
    return React.useMemo(() => {
      if (!products) {
        return [];
      }

      switch (layoutExtension) {
        case LayoutExtension.DesktopLarge:
          return products.slice(0, NUMBER_TO_SHOW_TOP_BLOCKS_INITIALLY_ON_DESKTOP);
        case LayoutExtension.DesktopSmall:
          return products.slice(0, NUMBER_TO_SHOW_TOP_BLOCKS_INITIALLY_ON_LAPTOP);
      }

      return products.slice(0, NUMBER_TO_SHOW_TOP_BLOCKS_INITIALLY_ON_MOBILE);
    }, [products, layoutExtension]);
  },

  useFilterByPage(isQuery_v1?: boolean): GiftCardProductsPageFilterInput | null {
    const entity = messageHooks.useSubjectState(GlobalMessages.PageEntity);

    return React.useMemo(() => {
      if (
        entity?.data?.__typename === 'CB_RedeemResultPage'
        || entity?.data?.__typename === 'CB_RedeemB2BResultPage'
      ) {
        return {
          fieldName: entity.data.productsListField_v1,
          pageId: entity.data.id,
          pageType: entity?.data?.__typename,
        };
      }

      if (
        isQuery_v1 && entity?.data?.__typename === 'GiftcardCategory'
      ) {
        return {
          fieldName: entity.data.productsListField,
          pageId: entity.data.id,
          pageType: entity?.data?.__typename,
        };
      }

      return null;
    }, [entity, isQuery_v1]);
  },

  useSetDataCallBack() {
    return React.useCallback((data: ProductsResponse | null, response: ProductsResponse) => {
      const allProducts = [...(data?.data.products ?? []), ...response.data.products];

      const uniqueProducts: (GiftcardProduct | DonationProduct | WrapperProduct |
        BlankGiftCardPackProduct | GiftPackingProduct |
         PersonalMessageProduct | DigitalWrapper)[] = [];

      allProducts.forEach((product => {
        const isProductAlreadyAdded = uniqueProducts.find(unique => unique.id === product.id);

        if (!isProductAlreadyAdded) {
          uniqueProducts.push(product);
        }
      }));

      return {
        ...response,
        data: {
          ...response.data,
          products: uniqueProducts,
        },
      };
    }, []);
  },

  useOnCompleted(setData: React.Dispatch<React.SetStateAction<ProductsResponse | null>>) {
    const setProductsResponse = messageHooks.useNext(ProductsListMessages.Products);
    const handleSetDataCallback = productsHooks.useSetDataCallBack();
    const prevState = messageHooks.useSubjectState(ProductsListMessages.Products);
    const ref = React.useRef(prevState);

    React.useEffect(() => {
      ref.current = prevState;
    }, [prevState]);
    return React.useCallback(
      (response: ProductsResponse | null) => {
        if (!response || !response.data) {
          return;
        }
        setProductsResponse(handleSetDataCallback(ref.current, response));
        setData(data => handleSetDataCallback(data, response));
      },
      [handleSetDataCallback, setData, setProductsResponse],
    );
  },
  useGetProductsContextCallback(passEncodedRedeemToken?: boolean) {
    return React.useCallback(() => {
      if (passEncodedRedeemToken) {
        return {
          context: {
            redeemToken: window.btoa(STATIC_REDEEM_TOKEN),
          },
        };
      } else {
        return {};
      }
    }, [passEncodedRedeemToken]);
  },
  useGetProductsVariables({
    filters,
    isRedeemPage,
    filterByPage,
    paging,
  }: UseGetProductsVariablesParams) {
    const filterEmpty = filterHooks.useFilterEmptyFilters();
    return React.useMemo(() => {
      if (!filters) {
        return;
      }
      return {
        filters: {
          filterByProductType:
            isRedeemPage
              ? [ProductType.GIFTCARD, ProductType.DONATION]
              : [ProductType.GIFTCARD],
          filterByTaxonomy: filterEmpty(filters),
          filterByPage,
        },
        paging,
      };
    }, [filters, isRedeemPage, filterEmpty, filterByPage, paging]);
  },
  useProducts({
    filters,
    page,
    toLoadProducts,
    isQuery_v1,
    passEncodedRedeemToken,
    query,
  }: UseProductsParams) {
    const { productsPerPage } = productsHooks.useProductsPerPage();
    const [data, setData] = React.useState<ProductsResponse | null>(null);
    const hasProductsRef = React.useRef<boolean | null>();
    const entity = messageHooks.useSubjectState(GlobalMessages.PageEntity);
    const onCompleted = productsHooks.useOnCompleted(setData);
    const getContext = productsHooks.useGetProductsContextCallback(passEncodedRedeemToken);
    React.useEffect(() => {
      hasProductsRef.current = !!data?.data.products.length;
    }, [data?.data.products.length]);

    const paging = React.useMemo(() => {
      const { current: hasProducts } = hasProductsRef;
      const pageNumber = page > 1 && hasProducts ? page : 1;
      const itemsPerPage = page > 1 && !hasProducts ? productsPerPage * page : productsPerPage;

      return { itemsPerPage, pageNumber };
    }, [page, productsPerPage]);

    const filterByPage = productsHooks.useFilterByPage(isQuery_v1);
    const isRedeemPage =
      entity?.data?.__typename === 'CB_RedeemResultPage'
      || entity?.data?.__typename === 'CB_RedeemB2BResultPage';
    const variables = productsHooks.useGetProductsVariables({
      paging,
      filterByPage,
      filters,
      isRedeemPage,
    });
    const {
      executeRequest,
      loading,
    } = getProductsHooks.useProductsQuery({
      context: getContext(),
      query,
      onCompleted,
    });

    const setProductsLoading = messageHooks.
      useNextWithValue(ProductsListMessages.ProductsLoading, loading);
    callbackHooks.useEffectCallback(setProductsLoading);

    React.useEffect(() => {
      if (!entity || !variables || !toLoadProducts) {
        return;
      }
      executeRequest({ variables });
    }, [executeRequest, variables, entity, toLoadProducts]);
    return { data, loading };
  },

  useProductsChunks(
    topProducts: TopBlock[] = [],
    products: (GiftcardProduct | DonationProduct)[] = [],
    page = 0,
  ): ProductsChunk[] {
    const { productsPerPage, topProductsPerPage } = productsHooks.useProductsPerPage();
    return React.useMemo(() => {
      const chunks = [];

      for (let i = 0; i < page; i++) {
        const topProductsStartSliceIdx = i * topProductsPerPage;
        const productsStartSliceIdx = i * productsPerPage;

        chunks.push({
          topProducts: topProducts.slice(
            topProductsStartSliceIdx,
            topProductsStartSliceIdx + topProductsPerPage,
          ),
          products: products.slice(productsStartSliceIdx, productsStartSliceIdx + productsPerPage),
        });
      }

      return chunks;
    }, [productsPerPage, topProductsPerPage, page, products, topProducts]);
  },

  useProductsFilters(): TaxonomyFilterInput[] {
    const parsedQuery = queryStringHooks.useParsedQueryString();
    return React.useMemo(() => {
      return Object.entries(parsedQuery)
        .filter(
          ([key]) =>
            key === CONFIG.vocabularyIdGiftcardCategories
            || key === CONFIG.vocabularyIdGiftcardType,
        )
        .map(([key, terms]) => ({
          vocabularyID: key as VocabularyID,
          terms: terms as unknown as string[],
        }));
    }, [parsedQuery]);
  },
  useShowMore(paging: Paging | undefined | null) {
    return React.useMemo(
      () => !!(paging && paging.currentPage * paging.itemsPerPage < paging.totalItems),
      [paging],
    );
  },
  useProductDataSetters() {
    const setDeliveryMethods = messageHooks.useNext(CheckoutMessages.DeliveryMethods);
    const setDeliveryMethod = messageHooks.useNext(CheckoutMessages.DeliveryMethod);
    const setDeliveryMethodType = messageHooks.useNext(CheckoutMessages.DeliveryMethodType);
    const setVariation = messageHooks.useNext(CheckoutMessages.Variation);
    const setFaqInfo = messageHooks.useNext(CheckoutMessages.FaqInfo);

    return {
      setDeliveryMethods,
      setDeliveryMethod,
      setDeliveryMethodType,
      setVariation,
      setFaqInfo,
    };
  },
  useToLoadProducts(isInitialLoad: boolean) {
    const [toLoadProducts, setToLoadProducts] = React.useState(!isInitialLoad);
    const toLoadProductsOnHandler = React.useCallback(() => setToLoadProducts(true), []);
    return { toLoadProducts, toLoadProductsOnHandler };
  },
  useMapFavoriteProducts(favoriteProducts: GiftcardProduct[] | undefined)
    :ProductListItemPresentational[] | null {
    return React.useMemo(() => {
      return favoriteProducts?.length ? (
        favoriteProducts.map((product) => (
          {
            ...product,
            cardImage: null,
            backgroundImage: null,
            type: ProductListItemType.GiftcardProduct,
          }))) : null;
    },[favoriteProducts]);
  },
};
